import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './FileTable.module.css';
import Modal from 'react-modal';
import GoToDashboardButton from '../components/GoToDashboardButton';    
import {
    deleteAllGlobalMetadataRecords,
    deleteGlobalMetadataRecord,
    deleteSelectedGlobalMetadataRecords,
    fetchAteSwVersions,
    fetchDatabaseRows,
    fetchDistinctSerialNumbers,
    fetchGlobalMetadataRecords,
    fetchTestSteps,
} from './fileTable/fileTableApi';
import { getDatePreset, sortRecordsByTestStarted } from './fileTable/fileTableData';

const FileTable = () => {
    const { t } = useTranslation();
    const [records, setRecords] = useState([]);
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [serialNumberOptions, setSerialNumberOptions] = useState([]);
    const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [ateSwVersion, setAteSwVersion] = useState('');
    const [ateSwVersions, setAteSwVersions] = useState([]);
    const [uutStatus, setUutStatus] = useState('');
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('desc'); // State for sorting order (asc or desc)
    const [isModalOpen, setIsModalOpen] = useState(false); // Modal state for displaying step details
    const [selectedFileSteps, setSelectedFileSteps] = useState([]); // State for selected file's step details
    const [loading, setLoading] = useState(true);  // Track loading state
    const [databaseRows, setDatabaseRows] = useState([]);
    const [databasePage, setDatabasePage] = useState(1);
    const [databaseTotalPages, setDatabaseTotalPages] = useState(1);
    const [databaseTotalRows, setDatabaseTotalRows] = useState(0);
    const [databaseLoading, setDatabaseLoading] = useState(false);
    const [showDatabaseRows, setShowDatabaseRows] = useState(false);
    const [stepNumberFilter, setStepNumberFilter] = useState('');

    const openHelp = () => setIsHelpOpen(true);
    const closeHelp = () => setIsHelpOpen(false);

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const openDeleteConfirm = () => setIsDeleteConfirmOpen(true);
    const closeDeleteConfirm = () => setIsDeleteConfirmOpen(false);

    const [isDeleteSelectedConfirmOpen, setIsDeleteSelectedConfirmOpen] = useState(false);
    const openDeleteSelectedConfirm = () => setIsDeleteSelectedConfirmOpen(true);
    const closeDeleteSelectedConfirm = () => setIsDeleteSelectedConfirmOpen(false);

    const [isDateModalOpen, setIsDateModalOpen] = useState(false);
    const [activeDateField, setActiveDateField] = useState(null); // 'start' or 'end'

    const [fileNameFilter, setFileNameFilter] = useState('');
    const databaseLimit = 100;

    
    // Fetch available ateSwVersions for the dropdown
    const loadFilterOptions = async () => {
        try {
            const [versions, serials] = await Promise.all([
                fetchAteSwVersions(),
                fetchDistinctSerialNumbers(),
            ]);
            setAteSwVersions(versions);
            setSerialNumberOptions(serials);
        } catch (error) {
            console.error('Error fetching file-table filter options:', error);
        }
    };

    // Fetch records with optional filters
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchGlobalMetadataRecords({
                fileNameFilter,
                selectedSerialNumbers,
                startDate,
                endDate,
                ateSwVersion,
                uutStatus,
            });
            setRecords(sortRecordsByTestStarted(data, sortOrder));
        } catch (error) {
            console.error('Error fetching records:', error);
        } finally {
            setLoading(false);
        }
    }, [ateSwVersion, endDate, fileNameFilter, selectedSerialNumbers, sortOrder, startDate, uutStatus]);

    const loadDatabaseRows = useCallback(async (page = 1) => {
        if (!showDatabaseRows) return;

        setDatabaseLoading(true);
        try {
            const data = await fetchDatabaseRows({
                fileNameFilter,
                selectedSerialNumbers,
                startDate,
                endDate,
                ateSwVersion,
                uutStatus,
                stepNumberFilter,
            }, page, databaseLimit);
            setDatabaseRows(data.rows || []);
            setDatabasePage(data.page || page);
            setDatabaseTotalPages(data.totalPages || 1);
            setDatabaseTotalRows(data.total || 0);
        } catch (error) {
            console.error('Error fetching database rows:', error);
            setDatabaseRows([]);
            setDatabaseTotalPages(1);
            setDatabaseTotalRows(0);
        } finally {
            setDatabaseLoading(false);
        }
    }, [
        ateSwVersion,
        endDate,
        fileNameFilter,
        selectedSerialNumbers,
        showDatabaseRows,
        startDate,
        stepNumberFilter,
        uutStatus,
    ]);

    useEffect(() => {
        loadFilterOptions();
    }, []);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    useEffect(() => {
        if (showDatabaseRows) {
            setDatabasePage(1);
            loadDatabaseRows(1);
        }
    }, [loadDatabaseRows, showDatabaseRows]);

    // Toggle sort order
    const toggleSortOrder = () => {
        setSortOrder(prevOrder => (prevOrder === 'desc' ? 'asc' : 'desc'));
    };

    // Fetch step details for a clicked file
    const fetchStepDetails = async (fileId) => {
        try {
            setSelectedFileSteps(await fetchTestSteps(fileId));
            setIsModalOpen(true); // Open modal when the file details are fetched
        } catch (error) {
            console.error('Error fetching step details:', error);
        }
    };

    // Delete a single record by ID
    const handleDelete = async (id) => {
        try {
            await deleteGlobalMetadataRecord(id);
            fetchRecords(); // Refresh records list
        } catch (error) {
            console.error('Error deleting record:', error);
        }
    };

    const confirmDeleteSelected = async () => {
    try {
        await deleteSelectedGlobalMetadataRecords(selectedRecords);
        setSelectedRecords([]); // Clear selection after deletion
        fetchRecords(); // Refresh list
    } catch (error) {
        console.error('Error deleting selected records:', error);
    } finally {
        closeDeleteSelectedConfirm();
    }
};

    // Delete all records
    const handleDeleteAll = async () => {
        try {
            await deleteAllGlobalMetadataRecords();
            setSelectedRecords([]); // Clear selection after deletion
            fetchRecords(); // Refresh records list
        } catch (error) {
            console.error('Error deleting all records:', error);
        }
    };
    

    const confirmDeleteAll = async () => {
  await handleDeleteAll();
  closeDeleteConfirm();
};
    // Handle selection of a record for bulk delete
    const handleSelectRecord = (id) => {
        setSelectedRecords((prev) =>
            prev.includes(id) ? prev.filter((recordId) => recordId !== id) : [...prev, id]
        );
    };

    
    const closeModal = () => setIsModalOpen(false); // Close modal for step details

    return (
        <div className={styles.filesTableContainer}>
            <GoToDashboardButton />
            {loading ? (
                    <div className={styles.loadingScreen}>
                        <div className={styles.spinner}></div>
                    <p>{t('Loading...')}</p>
                </div>
            ) : (
                <div>
                    <header className={styles.pageHeader}>
                        <div>
                            <p className={styles.eyebrow}>{t('Data library')}</p>
                            <h2 className={styles.filesTableTitle}>{t('View All Files')}</h2>
                            <p className={styles.description}>{t('Review uploaded test sessions, inspect step data, and manage records used by the analysis pages.')}</p>
                        </div>
                    </header>
    
                    {/* Filter controls */}
                    <div className={styles.filterControls}>
                            <input
                                type="text"
                                value={fileNameFilter}
                                onChange={(e) => {
                                    setFileNameFilter(e.target.value);
                                    setDatabasePage(1);
                                }}
                                placeholder={t('Filter by File Name')}
                                className={styles.filterInput}
                            />
    <Select
        isMulti
        options={serialNumberOptions}
        value={selectedSerialNumbers}
        onChange={(values) => {
            setSelectedSerialNumbers(values || []);
            setDatabasePage(1);
        }}
        placeholder={t('Filter by Serial Number')}
        className={styles.multiSelect}
        classNamePrefix="fileTableSelect"
    />
    <button
    className={styles.filterInput}
    onClick={() => { setActiveDateField('start'); setIsDateModalOpen(true); }}
>
    {startDate ? startDate.toLocaleDateString() : t('Select Start Date')}
</button>
<button
    className={styles.filterInput}
    onClick={() => { setActiveDateField('end'); setIsDateModalOpen(true); }}
>
    {endDate ? endDate.toLocaleDateString() : t('Select End Date')}
</button>

    <select
        value={ateSwVersion}
        onChange={(e) => {
            setAteSwVersion(e.target.value);
            setDatabasePage(1);
        }}
        className={styles.filterInput}
    >
        <option value="">{t('Select AteSwVersion')}</option>
        {ateSwVersions.map((version) => (
            <option key={version.AteSwVersion} value={version.AteSwVersion}>
                {version.AteSwVersion}
            </option>
        ))}
    </select>
    <select
        value={uutStatus}
        onChange={(e) => {
            setUutStatus(e.target.value);
            setDatabasePage(1);
        }}
        className={styles.filterInput}
    >
        <option value="">{t('Select UUT Status')}</option>
        <option value="FAIL">FAIL</option>
        <option value="PASS">PASS</option>
        <option value="STOP">STOP</option>
        <option value="UNCOMPLETE">UNCOMPLETE</option>
    </select>
    <input
        type="text"
        value={stepNumberFilter}
        onChange={(e) => {
            setStepNumberFilter(e.target.value);
            setDatabasePage(1);
        }}
        placeholder={t('Filter database rows by Step Number')}
        className={styles.filterInput}
    />
</div>

    
                    {/* Sort order toggle */}
                    <div className={styles.sortOrderToggle}>
                        <button onClick={toggleSortOrder} className={styles.filterButton}>
                            {t('Sort {{order}}', { order: sortOrder === 'desc' ? t('Oldest to Newest') : t('Newest to Oldest') })}
                        </button>
                        <button
                            onClick={() => setShowDatabaseRows((current) => !current)}
                            className={styles.filterButton}
                        >
                            {showDatabaseRows ? t('Hide Database Data') : t('Show All Database Data')}
                        </button>
                    </div>
    
                    {/* Action buttons */}
                    <div className={styles.actionsContainer}>
                        <button
            onClick={openDeleteSelectedConfirm}
            className={`${styles.actionButton} ${styles.deleteAll}`}
            disabled={!selectedRecords.length}
        >
            {t('Delete Selected')}
        </button>

        {/* Delete Selected Confirmation Modal */}
        <Modal
            isOpen={isDeleteSelectedConfirmOpen}
            onRequestClose={closeDeleteSelectedConfirm}
            className={styles.modalContent}
            overlayClassName={styles.modalOverlay}
            contentLabel={t('Confirm Delete Selected')}
        >
            <h2>{t('Confirm Deletion')}</h2>
            <p>
                {t('Are you sure you want to delete the selected')}
                {" "}
                <strong>{selectedRecords.length}</strong> record(s)?  
                {" "}
                {t('This action cannot be undone.')}
            </p>
            <div className={styles.modalActions}>
                <button
                    onClick={confirmDeleteSelected}
                    className={`${styles.actionButton} ${styles.deleteAll}`}
                >
                    {t('Yes, Delete Selected')}
                </button>
                <button
                    onClick={closeDeleteSelectedConfirm}
                    className={styles.closeModalButton}
                >
                    {t('Cancel')}
                </button>
            </div>
        </Modal>

                        {/* Delete All Button */}
            <button
                onClick={openDeleteConfirm}
                className={`${styles.actionButton} ${styles.deleteAll}`}
            >
                {t('Delete All')}
            </button>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteConfirmOpen}
                onRequestClose={closeDeleteConfirm}
                className={styles.modalContent}
                overlayClassName={styles.modalOverlay}
                contentLabel={t('Confirm Delete All')}
            >
                <h2>{t('Confirm Deletion')}</h2>
                <p>{t('Are you sure you want to delete all records? This action cannot be undone.')}</p>
                <div className={styles.modalActions}>
                    <button
                        onClick={confirmDeleteAll}
                        className={`${styles.actionButton} ${styles.deleteAll}`}
                    >
                        {t('Yes, Delete All')}
                    </button>
                    <button
                        onClick={closeDeleteConfirm}
                        className={styles.closeModalButton}
                    >
                        {t('Cancel')}
                    </button>
                </div>
            </Modal>
                    </div>
    
                    {/* Records table */}
                    <div className={styles.tableScroll}>
                    <table className={styles.filesTable}>
                        <thead>
                            <tr>
                                <th>{t('Select')}</th>
                                <th>{t('ID')}</th>
                                <th>{t('Filename')}</th>
                                <th>{t('Machine')}</th>
                                <th>{t('Station')}</th>
                                <th>{t('Serial Number')}</th>
                                <th>{t('UUT Status')}</th>
                                <th>{t('Test Started')}</th>
                                <th>{t('Test Stopped')}</th>
                                <th>{t('ATE SW Version')}</th>
                                <th>{t('Testspec')}</th>
                                <th>{t('Step Rows')}</th>
                                <th>{t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.length > 0 ? (
                                records.map((record) => (
                                    <tr key={record.id} className={selectedRecords.includes(record.id) ? styles.selectedRow : ''}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedRecords.includes(record.id)}
                                                onChange={() => handleSelectRecord(record.id)}
                                            />
                                        </td>
                                        <td>{record.id}</td>
                                        <td>{record.filename}</td>
                                        <td>{record.machineIdentifier || 'N/A'}</td>
                                        <td>{record.stationName || 'N/A'}</td>
                                        <td>{record.serialNumber}</td>
                                        <td>{record.uutStatus}</td>
                                        <td>{record.testStarted}</td>
                                        <td>{record.testStopped}</td>
                                        <td>{record.AteSwVersion || 'N/A'}</td>
                                        <td>{record.Testspec || 'N/A'}</td>
                                        <td>{record.stepCount ?? 0}</td>
                                        <td>
                                            <button
                                                onClick={() => fetchStepDetails(record.id)}
                                                className={styles.viewStepsButton}
                                            >
                                                {t('View Steps')}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(record.id)}
                                                className={styles.deleteButton}
                                            >
                                                {t('Delete')}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="13" className={styles.noFilesText}>
                                        {t('No records found.')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    </div>

                    {showDatabaseRows && (
                        <section className={styles.databasePanel}>
                            <div className={styles.databaseHeader}>
                                <div>
                                    <p className={styles.eyebrow}>{t('Raw database data')}</p>
                                    <h3 className={styles.databaseTitle}>{t('Global metadata joined with step data')}</h3>
                                    <p className={styles.description}>
                                        {t('Showing {{shown}} of {{total}} step rows. Filters above apply to this table too.', {
                                            shown: databaseRows.length,
                                            total: databaseTotalRows,
                                        })}
                                    </p>
                                </div>
                                <div className={styles.paginationControls}>
                                    <button
                                        className={styles.actionButton}
                                        disabled={databaseLoading || databasePage <= 1}
                                        onClick={() => loadDatabaseRows(databasePage - 1)}
                                    >
                                        {t('Previous')}
                                    </button>
                                    <span className={styles.pageText}>{t('Page {{page}} / {{total}}', { page: databasePage, total: databaseTotalPages })}</span>
                                    <button
                                        className={styles.actionButton}
                                        disabled={databaseLoading || databasePage >= databaseTotalPages}
                                        onClick={() => loadDatabaseRows(databasePage + 1)}
                                    >
                                        {t('Next')}
                                    </button>
                                </div>
                            </div>

                            {databaseLoading ? (
                                <p className={styles.loadingText}>{t('Loading database rows...')}</p>
                            ) : (
                                <div className={styles.tableScroll}>
                                    <table className={styles.databaseTable}>
                                        <thead>
                                            <tr>
                                                <th>{t('File ID')}</th>
                                                <th>{t('Filename')}</th>
                                                <th>{t('Machine')}</th>
                                                <th>{t('Station')}</th>
                                                <th>{t('Serial')}</th>
                                                <th>{t('UUT Status')}</th>
                                                <th>{t('Started')}</th>
                                                <th>{t('Stopped')}</th>
                                                <th>{t('ATE SW')}</th>
                                                <th>{t('Testspec')}</th>
                                                <th>{t('Step ID')}</th>
                                                <th>{t('Step Number')}</th>
                                                <th>{t('Step Result')}</th>
                                                <th>{t('Step Name')}</th>
                                                <th>{t('Step Time')}</th>
                                                <th>{t('Measure Type')}</th>
                                                <th>{t('Units')}</th>
                                                <th>{t('Measure Value')}</th>
                                                <th>{t('Limit Low')}</th>
                                                <th>{t('Limit High')}</th>
                                                <th>{t('Measure String')}</th>
                                                <th>{t('Limit String')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {databaseRows.length > 0 ? (
                                                databaseRows.map((row) => (
                                                    <tr key={`${row.fileId}-${row.stepId}`}>
                                                        <td>{row.fileId}</td>
                                                        <td>{row.filename}</td>
                                                        <td>{row.machineIdentifier || 'N/A'}</td>
                                                        <td>{row.stationName || 'N/A'}</td>
                                                        <td>{row.serialNumber || 'N/A'}</td>
                                                        <td>{row.uutStatus || 'N/A'}</td>
                                                        <td>{row.testStarted || 'N/A'}</td>
                                                        <td>{row.testStopped || 'N/A'}</td>
                                                        <td>{row.AteSwVersion || 'N/A'}</td>
                                                        <td>{row.Testspec || 'N/A'}</td>
                                                        <td>{row.stepId}</td>
                                                        <td>{row.stepNumber || 'N/A'}</td>
                                                        <td>{row.stepResult || 'N/A'}</td>
                                                        <td>{row.stepName || 'N/A'}</td>
                                                        <td>{row.stepTime || 'N/A'}</td>
                                                        <td>{row.measureType || 'N/A'}</td>
                                                        <td>{row.units || 'N/A'}</td>
                                                        <td>{row.measureValue || 'N/A'}</td>
                                                        <td>{row.limitLow || 'N/A'}</td>
                                                        <td>{row.limitHigh || 'N/A'}</td>
                                                        <td>{row.measureString || 'N/A'}</td>
                                                        <td>{row.limitString || 'N/A'}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="22" className={styles.noFilesText}>{t('No database rows found.')}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    )}
    
                    {/* Step Details Modal */}
                    <Modal
                        isOpen={isModalOpen}
                        onRequestClose={closeModal}
                        className={styles.modalContent}
                        overlayClassName={styles.modalOverlay}
                        contentLabel={t('Step Details')}
                    >
                        <h2>{t('Step Details')}</h2>
                        <button onClick={closeModal} className={styles.closeModalButton}>{t('Close')}</button>
                        <table className={styles.stepDetailsTable}>
                            <thead>
                                <tr>
                                    <th>{t('ID')}</th>
                                    <th>{t('Step Number')}</th>
                                    <th>{t('Step Result')}</th>
                                    <th>{t('Step Name')}</th>
                                    <th>{t('Step Time')}</th>
                                    <th>{t('Measure Type')}</th>
                                    <th>{t('Units')}</th>
                                    <th>{t('Measure Value')}</th>
                                    <th>{t('Limit Low')}</th>
                                    <th>{t('Limit High')}</th>
                                    <th>{t('Measure String')}</th>
                                    <th>{t('Limit String')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedFileSteps.length > 0 ? (
                                    selectedFileSteps.map((step, index) => (
                                        <tr key={index}>
                                            <td>{step.id}</td>
                                            <td>{step.stepNumber}</td>
                                            <td>{step.stepResult}</td>
                                            <td>{step.stepName || 'N/A'}</td>
                                            <td>{step.stepTime || 'N/A'}</td>
                                            <td>{step.measureType || 'N/A'}</td>
                                            <td>{step.units || 'N/A'}</td>
                                            <td>{step.measureValue || 'N/A'}</td>
                                            <td>{step.limitLow || 'N/A'}</td>
                                            <td>{step.limitHigh || 'N/A'}</td>
                                            <td>{step.measureString || 'N/A'}</td>
                                            <td>{step.limitString || 'N/A'}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="12">{t('No steps available.')}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </Modal>
    
                    {/* Help Button and Modal */}
                    <button className={styles.helpButton} onClick={openHelp}>?</button>
                    <Modal
    isOpen={isHelpOpen}
    onRequestClose={closeHelp}
    className={styles.helpModalContent}
    overlayClassName={styles.helpModalOverlay}
    contentLabel={t('Help Information')}
>
    <button onClick={closeHelp} className={styles.closeHelpButton}>
        {t('Close')}
    </button>
    <div className={styles.helpText}>
        <h2>{t('About this Page')}</h2>
        <p>{t('This page allows you to view, filter, and manage test records based on metadata extracted from uploaded files.')}</p>
        <ul>
            <li>
                <strong>{t('Filters')}:</strong> {t('You can filter the records by Serial Number, Start Date, End Date, ATE Software Version (AteSwVersion), and UUT Status (e.g., PASS, FAIL, STOP).')}
            </li>
            <li>
                <strong>{t('Sorting')}:</strong> {t('The records can be sorted by the Test Start Date, with options to view records from Newest to Oldest or Oldest to Newest.')}
            </li>
            <li>
                <strong>{t('Step Details')}:</strong> {t('For each record, you can view detailed step results by clicking the "View Steps" button. This shows the individual steps and their results for the associated test.')}
            </li>
            <li>
                <strong>{t('Bulk Actions')}:</strong> {t('You can delete selected records or delete all records with the action buttons at the top of the page.')}
            </li>
            <li>
                <strong>{t('Help')}:</strong> {t('You can open this help modal anytime by clicking the "?" button.')}
            </li>
        </ul>
    </div>
</Modal>
<Modal
    isOpen={isDateModalOpen}
    onRequestClose={() => setIsDateModalOpen(false)}
    className={styles.modalContent}
    overlayClassName={styles.modalOverlay}
    contentLabel={t('Select Date')}
>
    <h2>{t('Select {{field}} Date', { field: activeDateField === 'start' ? t('Start') : t('End') })}</h2>

    {/* Quick Select Buttons */}
    <div className={styles.quickSelectContainer}>
        <button
            onClick={() => {
                const today = new Date();
                activeDateField === 'start' ? setStartDate(today) : setEndDate(today);
                setIsDateModalOpen(false);
            }}
            className={styles.quickSelectButton}
        >
            {t('Today')}
        </button>

        <button
            onClick={() => {
                const yesterday = getDatePreset('yesterday');
                activeDateField === 'start' ? setStartDate(yesterday) : setEndDate(yesterday);
                setIsDateModalOpen(false);
            }}
            className={styles.quickSelectButton}
        >
            {t('Yesterday')}
        </button>

        <button
            onClick={() => {
                const sevenDaysAgo = getDatePreset('sevenDaysAgo');
                activeDateField === 'start' ? setStartDate(sevenDaysAgo) : setEndDate(sevenDaysAgo);
                setIsDateModalOpen(false);
            }}
            className={styles.quickSelectButton}
        >
            {t('7 Days Ago')}
        </button>

        <button
            onClick={() => {
                const thirtyDaysAgo = getDatePreset('thirtyDaysAgo');
                activeDateField === 'start' ? setStartDate(thirtyDaysAgo) : setEndDate(thirtyDaysAgo);
                setIsDateModalOpen(false);
            }}
            className={styles.quickSelectButton}
        >
            {t('30 Days Ago')}
        </button>

        <button
            onClick={() => {
                activeDateField === 'start' ? setStartDate(null) : setEndDate(null);
                setIsDateModalOpen(false);
            }}
            className={`${styles.quickSelectButton} ${styles.clearButton}`}
        >
            {t('Clear')}
        </button>
    </div>

    {/* Inline Date Picker */}
    <DatePicker
        selected={activeDateField === 'start' ? startDate : endDate}
        onChange={(date) => {
            if (activeDateField === 'start') setStartDate(date);
            else setEndDate(date);
            setIsDateModalOpen(false);
        }}
        inline
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
    />

    <div className={styles.modalActions}>
        <button
            onClick={() => setIsDateModalOpen(false)}
            className={styles.closeModalButton}
        >
            {t('Cancel')}
        </button>
    </div>
</Modal>


                </div>
            )}
        </div>
    );
    
};

export default FileTable;
